<template>
  <div class="mt-1">
    <b-card>
      <b-overlay :show="loading" rounded="sm" no-fade>
        <b-row class="mb-1">
          <!-- Per Page -->
          <b-col
            cols="12"
            md="7"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              variant="outline-primary"
              class="btn-icon"
              :to="{ path: '/settings' }"
            >
              <i class="fa-solid fa-arrow-left"></i>
              <span class="ml-25">Back</span>
            </b-button>

            <h3 class="ml-1">Working Groups</h3>
          </b-col>

          <b-col cols="12" md="5">
            <div class="d-flex align-items-center justify-content-end">
              <div class="d-flex align-items-center mr-1">
                <label>Show</label>
                <v-select
                  v-model="perPage"
                  :options="perPageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label>entries </label>
              </div>

              <b-form-input
                v-model="filter"
                class="d-inline-block mr-1"
                cle
                placeholder="Search..."
              />
              <b-button
                v-if="$Can('add-working-group')"
                variant="primary"
                @click="addWorkingGroup()"
              >
                <span class="text-nowrap">Add Working Group</span>
              </b-button>
            </div>
          </b-col>
        </b-row>

        <b-table
          sticky-header
          striped
          hover
          bordered
          ref="refActionListTable"
          :outlined="true"
          :fields="fields"
          :items="filteredWorkingGroups"
          responsive="sm"
          :small="true"
          class="table table-sm table-responsive"
          :filter="filter"
          @filtered="onFiltered"
          :filter-included-fields="filterOn"
          show-empty
          empty-text="No matching records found"
          :current-page="currentPage"
          :per-page="perPage"
          @row-clicked="
            $Can('edit-working-group') && editWorkingGroup($event.id)
          "
        >
          <template #cell(terms_of_reference)="data">
            <div style="font-size: 1rem;" class="p-25">
              <span :id="`terms-${data.index}`" class="terms-reference-text">
                {{
                  data.value
                    ? data.value.length > 180
                      ? data.value.substring(0, 180) + "..."
                      : data.value
                    : "-"
                }}
              </span>
              <b-popover
                v-if="data.value && data.value.length > 180"
                :target="`terms-${data.index}`"
                triggers="hover"
                placement="auto"
                custom-class="terms-popover"
                boundary="viewport"
              >
                <template #title>Terms of Reference</template>
                <div class="terms-popover-content">{{ data.value }}</div>
              </b-popover>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge
              v-if="data.item.status == 'Active'"
              pill
              :variant="`light-success`"
              class="text-capitalize"
            >
              {{ data.item.status }}
            </b-badge>

            <b-badge
              v-else
              pill
              :variant="`light-danger`"
              class="text-capitalize"
            >
              {{ data.item.status }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div>
              <b-button
                v-if="$Can('edit-working-group')"
                variant="warning"
                class="mr-25 p-50"
                @click="editWorkingGroup(data.item.id)"
              >
                <i class="fa-solid fa-pen-to-square mr-25"></i>
                <span> Edit</span>
              </b-button>

              <b-button
                v-if="$Can('change-status-working-group')"
                variant="primary"
                @click="changeStatus(data.item.id)"
                class="mr-25 p-50"
                v-b-tooltip.hover.bottom="
                  data.item.status === 'Active'
                    ? 'If you passive this working group, it will not show in the list'
                    : 'If you active this working group, it will show in the list'
                "
              >
                <span>{{
                  data.item.status === "Active" ? "Passive" : "Active"
                }}</span>
              </b-button>

              <b-button
                v-if="$Can('delete-working-group')"
                variant="danger"
                @click="deleteWorkingGroup(data.item.id)"
                class="p-50"
              >
                <span>Delete</span>
              </b-button>
            </div>
          </template>
        </b-table>

        <!-- Add pagination below table -->
        <div class="mx-2 mb-1 mt-1">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                {{ dataMeta.of }} entries</span
              >
            </b-col>

            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BOverlay,
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BFormRating,
  VBPopover,
} from "bootstrap-vue";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { onUnmounted } from "@vue/composition-api";
import settingsStoreModule from "../settingsStoreModule";
import vSelect from "vue-select";

export default {
  components: {
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BFormRating,
    vSelect,
    ToastificationContent,
  },
  directives: {
    "b-popover": VBPopover,
  },

  setup() {
    const WORKING_GROUP_APP_STORE_MODULE_NAME = "settings";

    if (!store.hasModule(WORKING_GROUP_APP_STORE_MODULE_NAME))
      store.registerModule(
        WORKING_GROUP_APP_STORE_MODULE_NAME,
        settingsStoreModule
      );

    onUnmounted(() => {
      if (store.hasModule(WORKING_GROUP_APP_STORE_MODULE_NAME))
        store.unregisterModule(WORKING_GROUP_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      workingGroups: [],
      filter: null,
      filterOn: ["group_name", "terms_of_reference", "start_date", "end_date"],
      loading: false,
      currentPage: 1,
      perPage: localStorage.getItem("workingGroupShow") ?? 50,
      totalRows: 0,

      perPageOptions: [10, 25, 50, 100],
      fields: [
        {
          key: "group_name",
          sortable: true,
          class: "text-left",
          thStyle: "width:20%;color:white!important",
        },

        {
          key: "terms_of_reference",
          sortable: true,
          class: "text-left",
          thStyle: "width:30%;color:white!important",
        },
        {
          key: "start_date",
          sortable: true,
          class: "text-center",
          thStyle: "width:10%;color:white!important",
          formatter: (value) => {
            if (!value || value === "Invalid Date") return "-";
            try {
              const date = new Date(value);
              if (isNaN(date.getTime())) return "-";
              return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              });
            } catch (e) {
              return "-";
            }
          },
        },
        {
          key: "end_date",
          sortable: true,
          class: "text-center",
          thStyle: "width:10%;color:white!important",
          formatter: (value) => {
            if (!value || value === "Invalid Date") return "-";
            try {
              const date = new Date(value);
              if (isNaN(date.getTime())) return "-";
              return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "2-digit",
              });
            } catch (e) {
              return "-";
            }
          },
        },
        {
          key: "status",
          sortable: false,
          class: "text-center",
          thStyle: "width:10%;color:white!important",
        },
        {
          key: "actions",
          sortable: false,
          class: "text-center",
          thStyle: "width:20%;color:white!important",
        },
      ],
    };
  },

  watch: {},

  methods: {
    getWorkingGroups() {
      this.loading = true;
      store
        .dispatch("settings/getAllWorkingGroups")
        .then((res) => {
          this.workingGroups = res.data;
          this.totalRows = res.data.length;
          this.loading = false;
        })
        .catch((error) => {
          this.$router.push({ name: "settings" });
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "An error occurred",
              text: "Please try again later or contact support.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },

    editWorkingGroup(id) {
      this.$router.push({
        name: "settings-working-group-edit",
        params: { id: id },
      });
    },

    changeStatus(id) {
      this.$bvModal
        .msgBoxConfirm(
          "Are you sure you want change status of this working group?",
          {
            size: "sm",
            title: "Confirm Deletion",
            okVariant: "primary",
            okTitle: "Yes",
            cancelTitle: "No",
            cancelVariant: "outline-secondary",
            hideHeaderClose: true,
            centered: true,
          }
        )
        .then((value) => {
          if (value) {
            this.loading = true;
            const workingGroup = this.workingGroups.find(
              (met) => met.id === id
            );

            if (workingGroup) {
              store
                .dispatch("settings/changeStatusWorkingGroup", workingGroup)
                .then((response) => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Successfully",
                      text: "✔️ Working group status changed Successfully",
                      icon: "ThumbsUpIcon",
                      variant: "success",
                    },
                  });

                  this.getWorkingGroups();
                })
                .catch((error) => {
                  this.formShow = false;
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "An error occurred",
                      text: "Please try again later or contact support.",
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  });
                });
            }
          }
        });
    },

    async deleteWorkingGroup(id) {
      const usage = await this.checkWorkingGroupUsage(id);

      let message = "Are you sure you want delete of this working group?";
      if (usage > 0) {
        message =
          "There are uses related to this working group. Do you still want to delete it? All related data will be deleted!";
      }
      this.$bvModal
        .msgBoxConfirm(message, {
          size: "sm",
          title: "Confirm Deletion",
          okVariant: "primary",
          okTitle: "Yes",
          cancelTitle: "No",
          cancelVariant: "outline-secondary",
          hideHeaderClose: true,
          centered: true,
        })
        .then((value) => {
          if (value) {
            this.loading = true;
            const workingGroup = this.workingGroups.find(
              (met) => met.id === id
            );

            if (workingGroup) {
              store
                .dispatch("settings/deleteWorkingGroup", workingGroup)
                .then((response) => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "Successfully",
                      text: "✔️ Working group deleted Successfully",
                      icon: "ThumbsUpIcon",
                      variant: "success",
                    },
                  });

                  this.getWorkingGroups();
                })
                .catch((error) => {
                  this.formShow = false;
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: "An error occurred",
                      text: "Please try again later or contact support.",
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  });
                });
            }
          }
        });
    },

    checkWorkingGroupUsage(id) {
      return new Promise((resolve, reject) => {
        store.dispatch("settings/checkWorkingGroupUsage", id).then((res) => {
          resolve(res.data.usage_count);
        });
      });
    },

    addWorkingGroup() {
      this.$router.push({ name: "settings-working-group-add" });
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    downloadDocument(document) {
      window.open(document, "_blank");
    },
  },

  created() {
    this.getWorkingGroups();
  },

  computed: {
    dataMeta() {
      return {
        from:
          this.totalRows != 0 ? this.perPage * (this.currentPage - 1) + 1 : 0,
        to:
          this.perPage * (this.currentPage - 1) + Number(this.perPage) <
          this.totalRows
            ? this.perPage * (this.currentPage - 1) + Number(this.perPage)
            : this.totalRows,
        of: this.totalRows,
      };
    },
    filteredWorkingGroups() {
      let filtered = this.workingGroups;

      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        filtered = this.workingGroups.filter(
          (item) =>
            item.group_name.toLowerCase().includes(searchTerm) ||
            (item.terms_of_reference &&
              item.terms_of_reference.toLowerCase().includes(searchTerm))
        );
      }

      return filtered;
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.table-responsive.b-table-sticky-header {
  max-height: 75vh !important;
}

.terms-reference-text {
  cursor: pointer;
}
</style>

<style>
/* Add these new styles */
.terms-popover {
  max-width: 400px !important;
}

.terms-popover-content {
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 5px;
}

/* Customize scrollbar for the popover content */
.terms-popover-content::-webkit-scrollbar {
  width: 6px;
}

.terms-popover-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.terms-popover-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.terms-popover-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
